/**
 * Per-item forgemagie: overmaging a natural line past its max roll, and
 * adding "exo" lines the item does not naturally carry.
 *
 * The rules, as the game applies them (Dofus 2.x, unchanged in Dofus 3):
 *
 * - Every characteristic has a rune weight per point (`RUNE_WEIGHTS`).
 * - A line may be raised past its max roll ("over") only while the whole
 *   line weighs at most `MAGING_WEIGHT_LIMIT`. A line whose max roll already
 *   weighs more than that cannot be overmaged at all.
 * - An exo line weighs at most `MAGING_WEIGHT_LIMIT`.
 * - The item's over *portions* and exo lines share one budget of
 *   `MAGING_WEIGHT_LIMIT`. That is what caps an item at one exo PA/PM/PO
 *   (100 + 90 > 101) and leaves 11 weight of over beside an exo PM.
 * - Dofus, trophies, prysmaradites and pets are not forgemageable.
 *
 * Sources: huzounet.fr/guides/forgemagie, tofus.fr/fiches/forgemagie,
 * helleria.wordpress.com (overmax-et-exotique).
 *
 * A build stores the maging per slot as `{ [slot]: { itemId, lines } }`,
 * `lines` being `{ [characteristicId]: value }` — the line's final value for a
 * natural line, the whole line for an exo. The item id travels with it so a
 * slot that changes item loses the maging, whichever path changed it.
 */
import * as st from './st';
import { SLOT_IDX } from './slotIdx';
import { processItemStats } from './statProcessing';

// ── Knobs ────────────────────────────────────────────────────────────────
export const MAGING_WEIGHT_LIMIT = 101;

/** Rune weight of one point of each characteristic. Absent = no rune exists. */
export const RUNE_WEIGHTS = {
    [st.AP_ID]: 100,
    [st.MP_ID]: 90,
    [st.RANGE_ID]: 51,
    [st.SP_ID]: 30,
    [st.DAMAGE_ID]: 20,
    [st.SPELL_PER_DMG_ID]: 15,
    125: 15, // % melee damage
    120: 15, // % ranged damage
    122: 15, // % weapon damage
    124: 15, // % melee resistance
    121: 15, // % ranged resistance
    49: 10, // heals
    [st.CRIT_ID]: 10,
    [st.AP_REDUCTION_ID]: 7,
    [st.MP_REDUCTION_ID]: 7,
    [st.AP_PARRY_ID]: 7,
    [st.MP_PARRY_ID]: 7,
    37: 6, 33: 6, 34: 6, 35: 6, 36: 6, // % elemental resistances
    92: 5, 88: 5, 89: 5, 90: 5, 91: 5, // elemental damage
    [st.CRIT_DAMAGE_ID]: 5,
    84: 5, // pushback damage
    [st.LOCK_ID]: 4,
    [st.DODGE_ID]: 4,
    [st.PROSPECTING_ID]: 3,
    [st.WISDOM_ID]: 3,
    [st.POWER_ID]: 2,
    58: 2, 54: 2, 55: 2, 56: 2, 57: 2, // fixed elemental resistances
    87: 2, // critical resistance
    85: 2, // pushback resistance
    [st.STRENGTH_ID]: 1,
    [st.INTELLIGENCE_ID]: 1,
    [st.CHANCE_ID]: 1,
    [st.AGILITY_ID]: 1,
    [st.VITALITY_ID]: 0.2,
    [st.PODS_ID]: 0.25,
    [st.INITIATIVE_ID]: 0.1,
};

export const MAGABLE_SLOTS = [
    SLOT_IDX.HAT,
    SLOT_IDX.CLOAK,
    SLOT_IDX.AMULET,
    SLOT_IDX.RING1,
    SLOT_IDX.RING2,
    SLOT_IDX.BELT,
    SLOT_IDX.BOOTS,
    SLOT_IDX.WEAPON,
    SLOT_IDX.SHIELD,
];

/**
 * Forgemagie bonus a character may carry in total, per characteristic: +1 AP,
 * +1 MP, +1 Range, however it is spread between the character's exos and the
 * items. Past it a maged point is kept on the item but adds nothing.
 */
export const MAGING_BONUS_CAPS = {
    [st.AP_ID]: 1,
    [st.MP_ID]: 1,
    [st.RANGE_ID]: 1,
};

/**
 * Level from which a build lets generations use transcendence runes until the
 * user says otherwise (`Build.isTranscendenceOn`).
 */
export const TRANSCENDENCE_DEFAULT_LEVEL = 200;

/** The exos a generation may place on items — the capped ones — in menu order. */
export const FM_EXO_IDS = [st.AP_ID, st.MP_ID, st.RANGE_ID];

/** The ones a fresh build places: +1 AP and MP, as most players forge. */
export const DEFAULT_FM_EXOS = [st.AP_ID, st.MP_ID];

/** Float weights (0.2, 0.1) make 101 / w land a hair under the integer. */
const WEIGHT_EPSILON = 1e-9;
// ─────────────────────────────────────────────────────────────────────────

export const MAGING_KIND = { OVER: 'over', EXO: 'exo' };

/**
 * Key under which `Build.getItemMaging` tags a slot's lines with the
 * characteristic a transcendence rune put there, so `magedStatRows` can mark
 * that row "(Trans)" wherever the lines travel. A Symbol: JSON, Object.entries
 * and every loop over the lines skip it, so nothing else sees an extra line.
 */
export const TRANS_LINE = Symbol('transLine');

export const isSlotMagable = (slot) => MAGABLE_SLOTS.includes(Number(slot));
export const runeWeight = (characId) => RUNE_WEIGHTS[Number(characId)] ?? null;
export const isCharacMagable = (characId) => runeWeight(characId) !== null;

const pointsFor = (weight, w) => Math.floor(weight / w + WEIGHT_EPSILON);

/**
 * The exos an old `{ characId: count }` map stands for — the stats panel's +1
 * shortcuts a build saved before they were removed, or a chat draft's. A key
 * it leaves out has the fresh-build default.
 */
export function fmExosFromShortcuts(map) {
    return FM_EXO_IDS.filter((id) => Number(map?.[id] ?? (DEFAULT_FM_EXOS.includes(id) ? 1 : 0)) >= 1);
}

/**
 * `itemMaging` with a saved build's old +1 shortcuts turned into exo lines on
 * its items, so its sheet keeps the points they gave. Each goes where a
 * generation would put it — a ring first for AP and MP, off the rings for
 * Range — on an item with no maging yet that can take it; none goes when an
 * item's maging already adds that characteristic (the shortcut was redundant)
 * or no item can take it. Marked `exo`, so the next generation may move it.
 */
export function forgeShortcutExos(itemMaging, items, itemIds, shortcuts) {
    const out = { ...(itemMaging || {}) };
    const rings = [SLOT_IDX.RING1, SLOT_IDX.RING2];
    const others = MAGABLE_SLOTS.filter((slot) => !rings.includes(slot));
    const added = (id) => Object.entries(out).some(([slot, entry]) => (
        (magingDelta(items?.[slot], entry.lines)[id] || 0) > 0
    ));
    fmExosFromShortcuts(shortcuts).forEach((id) => {
        if (added(id)) return;
        const order = id === st.RANGE_ID ? [...others, ...rings] : [...rings, ...others];
        // An exo is a line the item does not carry: a natural one would be
        // overwritten, not added to.
        const slot = order.find((s) => items?.[s] && !out[s] && !naturalLines(items[s])[id]
            && withLine(items[s], null, id, 1)[id] === 1);
        if (slot !== undefined) out[slot] = { itemId: Number(itemIds[slot]), lines: { [id]: 1 }, exo: id };
    });
    return out;
}

/**
 * The item's own lines: `{ [characId]: { min, max } }`, `max` being the best
 * roll (the value `effects_map` carries). Rolls come from `effects`; an item
 * with only `effects_map` gets its value as both ends.
 */
export function naturalLines(item) {
    const out = {};
    const map = item?.effects_map || {};
    Object.entries(map).forEach(([rawId, rawValue]) => {
        const value = Number(rawValue);
        if (!Number.isFinite(value) || value === 0) return;
        out[Number(rawId)] = { min: value, max: value };
    });
    (Array.isArray(item?.effects) ? item.effects : []).forEach((effect) => {
        const id = Number(effect?.characteristic);
        const line = out[id];
        if (!line) return;
        const lo = Number(effect.min);
        const hi = Number(effect.max);
        if (Number.isFinite(lo) && Number.isFinite(hi)) {
            line.min = Math.min(lo, hi, line.max);
        }
    });
    return out;
}

/** What a line is once maged: 'over', 'exo', or null for a plain roll. */
export function lineKind(item, characId, value) {
    const natural = naturalLines(item)[Number(characId)];
    if (!natural) return value ? MAGING_KIND.EXO : null;
    return Number(value) > natural.max ? MAGING_KIND.OVER : null;
}

/** Budget weight a line uses: its over portion, or the whole exo line. */
function lineBudgetWeight(natural, characId, value) {
    const w = runeWeight(characId);
    if (!w || !Number.isFinite(value)) return 0;
    if (!natural) return Math.max(0, value) * w;
    return Math.max(0, value - Math.max(natural.max, 0)) * w;
}

/** Over + exo weight the item carries, optionally leaving one line out. */
export function magingWeight(item, lines, exceptCharacId = null) {
    const naturals = naturalLines(item);
    return Object.entries(lines || {}).reduce((sum, [rawId, value]) => {
        const id = Number(rawId);
        if (id === Number(exceptCharacId)) return sum;
        return sum + lineBudgetWeight(naturals[id], id, Number(value));
    }, 0);
}

/**
 * The values a line may take, given the item's other maged lines — or null
 * when the characteristic has no rune. Natural lines go down to 0 (a line can
 * be forged away); a malus line only moves between its worst roll and 0.
 */
export function lineRange(item, lines, characId) {
    const w = runeWeight(characId);
    if (!w) return null;
    const natural = naturalLines(item)[Number(characId)];
    const budget = Math.max(0, MAGING_WEIGHT_LIMIT - magingWeight(item, lines, characId));
    if (!natural) return { min: 0, max: pointsFor(Math.min(MAGING_WEIGHT_LIMIT, budget), w) };
    if (natural.max <= 0) return { min: natural.min, max: 0 };
    const lineCap = Math.max(natural.max, pointsFor(MAGING_WEIGHT_LIMIT, w));
    return { min: 0, max: Math.min(lineCap, natural.max + pointsFor(budget, w)) };
}

/**
 * Stat ids in the order the stats tab shows them: section by section, each
 * section's left column then its right one. Stats the tab does not show
 * follow, by their `order`.
 */
const STATS_TAB_ORDER = st.resolveCompactPanelGroups(st.STAT_COMPACT_DESKTOP_LAYOUT)
    .flatMap((group) => group.columns.flat())
    .filter(Boolean)
    .map((stat) => stat.id);
const statsTabRank = (stat) => {
    const index = STATS_TAB_ORDER.indexOf(stat.id);
    return index >= 0 ? index : STATS_TAB_ORDER.length + stat.order;
};

/** Characteristics the item could take as a new exo line right now, in stats-tab order. */
export function exoCandidates(item, lines) {
    const naturals = naturalLines(item);
    return st.STATS
        .filter((s) => isCharacMagable(s.id) && !naturals[s.id] && !(lines || {})[s.id])
        .filter((s) => (lineRange(item, lines, s.id)?.max || 0) > 0)
        .sort((a, b) => statsTabRank(a) - statsTabRank(b));
}

/**
 * `lines` with one line set to `value`, clamped to its range. A natural line
 * back at its max roll and an exo at 0 leave the map: neither is a maging.
 */
export function withLine(item, lines, characId, value) {
    const id = Number(characId);
    const range = lineRange(item, lines, id);
    const next = { ...(lines || {}) };
    if (!range) return next;
    const n = Math.round(Number(value));
    const clamped = Number.isFinite(n) ? Math.max(range.min, Math.min(range.max, n)) : 0;
    const natural = naturalLines(item)[id];
    if ((natural && clamped === natural.max) || (!natural && clamped === 0)) delete next[id];
    else next[id] = clamped;
    return next;
}

/** The item's effects map once maged: natural lines replaced, exo lines added. */
export function magedEffectsMap(item, lines) {
    const base = {};
    Object.entries(item?.effects_map || {}).forEach(([rawId, value]) => {
        const n = Number(value);
        if (Number.isFinite(n) && n !== 0) base[Number(rawId)] = n;
    });
    if (!lines) return base;
    Object.entries(lines).forEach(([rawId, value]) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return;
        if (n === 0) delete base[Number(rawId)];
        else base[Number(rawId)] = n;
    });
    return base;
}

/**
 * The item's stat rows once maged, each tagged 'over' / 'exo'. A malus line
 * maged up to 0 (-1 Range → 0) is an over and stays listed. With `editable`
 * every forgeable row carries its `range`, and a natural line forged down to
 * 0 stays listed too, so it can be raised back.
 */
export function magedStatRows(item, maging, editable) {
    const trans = maging?.[TRANS_LINE];
    const rows = processItemStats(maging ? magedEffectsMap(item, maging) : item.effects_map)
        .map((row) => ({
            ...row,
            value: Number(row.value),
            maging: lineKind(item, row.id, row.value),
            ...(trans != null && row.id === trans ? { trans: true } : {}),
        }));
    Object.keys(naturalLines(item)).forEach((rawId) => {
        const id = Number(rawId);
        const kind = lineKind(item, id, 0);
        if ((editable || kind) && !rows.some((r) => r.id === id)) {
            rows.push(...processItemStats({ [id]: 0 }).map((r) => ({ ...r, value: 0, maging: kind })));
        }
    });
    if (!editable) return rows;
    return rows.map((row) => ({ ...row, range: lineRange(item, maging, row.id) }));
}

/** What the maging adds to the item, per characteristic (may be negative). */
export function magingDelta(item, lines) {
    const base = item?.effects_map || {};
    const out = {};
    Object.entries(lines || {}).forEach(([rawId, value]) => {
        const d = Number(value) - (Number(base[rawId]) || 0);
        if (Number.isFinite(d) && d !== 0) out[Number(rawId)] = d;
    });
    return out;
}

/**
 * What goes past `MAGING_BONUS_CAPS`: `{ [characId]: points over }`, from the
 * character's exos and the `magingDelta` of each maged item.
 */
export function magingBonusExcess(exos, itemDeltas) {
    const out = {};
    Object.entries(MAGING_BONUS_CAPS).forEach(([id, cap]) => {
        const total = (Number(exos?.[id]) || 0)
            + (itemDeltas || []).reduce((sum, delta) => sum + (Number(delta?.[id]) || 0), 0);
        if (total > cap) out[Number(id)] = total - cap;
    });
    return out;
}

/**
 * A stored maging map, cleaned against the build's item ids: entries for a
 * slot that cannot be maged, or that now holds a different item, are dropped.
 */
export function normalizeItemMaging(raw, itemIds) {
    const out = {};
    if (!raw || typeof raw !== 'object') return out;
    Object.entries(raw).forEach(([rawSlot, entry]) => {
        const slot = Number(rawSlot);
        if (!isSlotMagable(slot) || !entry || typeof entry !== 'object') return;
        const itemId = Number(entry.itemId);
        if (!Number.isFinite(itemId) || Number(itemIds?.[slot]) !== itemId) return;
        const lines = {};
        Object.entries(entry.lines || {}).forEach(([rawId, value]) => {
            const id = Number(rawId);
            const n = Number(value);
            if (isCharacMagable(id) && Number.isFinite(n)) lines[id] = n;
        });
        if (!Object.keys(lines).length) return;
        out[slot] = { itemId, lines };
        // The line a transcendence rune put there, or the exo a generation
        // placed there (withSolverMaging) — marked so the next run can redo it.
        ['trans', 'exo'].forEach((key) => {
            const id = Number(entry[key]);
            if (entry[key] != null && lines[id] !== undefined) out[slot][key] = id;
        });
    });
    return out;
}

/**
 * Items a character may transcend: every mageable slot but one per exo — an
 * item holding an exo line cannot take a transcendence rune. `exos` is
 * `{ characId: count }`, each counted up to its forgemagie cap.
 */
export function transcendenceBudget(exos) {
    const used = Object.entries(MAGING_BONUS_CAPS).reduce(
        (n, [id, cap]) => n + Math.min(cap, Math.max(0, Number(exos?.[id]) || 0)),
        0,
    );
    return Math.max(0, MAGABLE_SLOTS.length - used);
}

/**
 * `itemMaging` once a generation's forgemagie is applied.
 *
 * `maging` is `{ runes, exos, slots }`, from the solver:
 * - `runes`: transcendence runes, `{ slot, charac, bonus }`, the bonus on top of
 *   the item's own line. A transcended item carries nothing else, so the rune
 *   replaces the slot's maging.
 * - `exos`: the exos it placed, `{ slot, charac }` — each on an item that could
 *   take it. A real line, counted like any other; marked `exo` only so the
 *   next run can move it.
 * - `slots`: the slots the run was free to use — every mageable one not
 *   locked. Runes and placed exos an earlier run left there go first; the run
 *   decided afresh. Slots it could not touch keep what they had.
 */
export function withSolverMaging(itemMaging, items, maging) {
    if (!maging) return itemMaging || {};
    const out = { ...(itemMaging || {}) };
    (maging.slots || []).forEach((slot) => {
        if (out[slot]?.trans != null || out[slot]?.exo != null) delete out[slot];
    });
    const write = (slot, charac, value, key) => {
        const item = items?.[slot];
        const itemId = Number(item?.id);
        if (!item || !Number.isFinite(itemId) || !isSlotMagable(slot)) return;
        out[slot] = { itemId, lines: { [charac]: value }, [key]: charac };
    };
    (maging.runes || []).forEach(({ slot, charac, bonus }) => {
        const id = Number(charac);
        const natural = naturalLines(items?.[slot])[id];
        write(slot, id, (natural ? natural.max : 0) + Number(bonus), 'trans');
    });
    (maging.exos || []).forEach(({ slot, charac }) => write(slot, Number(charac), 1, 'exo'));
    return out;
}
